// dashboard/views/PermissionsView.js
// Extracted from App.js (2026-08-12 split).

import { useTranslation } from 'react-i18next';
import { useState, useEffect } from 'react';
import { supabase } from '../../supabase';
import { c } from '../constants';

export default function PermissionsView({ clinic, profile, onGoToBilling }) {
  const { t } = useTranslation();
  const [members,  setMembers]  = useState([]);
  const [newRows,  setNewRows]  = useState([]);
  const [seatInfo, setSeatInfo] = useState(null);
  const [loading,         setLoading]         = useState(true);
  const [saving,          setSaving]          = useState(false);
  const [error,           setError]           = useState('');
  const [saved,           setSaved]           = useState(false);
  // Independent onboarding/billing audit (2026-09-10), release blocker #3:
  // this drives an explicit "Go to Billing" action on a seat/subscription
  // error, instead of leaving the manager to find Billing themselves.
  const [billingBlocked,  setBillingBlocked]  = useState(false);
  const [lastManagerBlocked, setLastManagerBlocked] = useState(null); // profileId blocking removal

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [clinic.id]);

  async function load() {
    setLoading(true);
    setError('');
    setBillingBlocked(false);
    try {
      // Narrow SECURITY DEFINER reader added in migration 171. Migration
      // 158 correctly removed cross-profile table SELECT (a raw profile row
      // contains far more than roster display fields), so an embedded
      // profiles query here was RLS-filtered to dashes for everyone except
      // the caller. The RPC returns only the safe roster columns.
      const { data, error: err } = await supabase.rpc('get_clinic_roster', {
        p_clinic_id: clinic.id,
      });

      if (err) throw err;

      setMembers((data || []).map(m => ({
        memberId:  m.member_id,
        profileId: m.profile_id,
        name:      [m.first_name, m.last_name].filter(Boolean).join(' ') || '—',
        email:     m.email || '—',
        isDoctor:  m.is_doctor  ?? false,
        isManager: m.is_manager ?? false,
        isPending: !m.is_active && !m.accepted_at,
        // Brand-new-Doctor invitations (audit finding #2, 2026-09-10):
        // get_clinic_roster (migration 212) merges pending clinic_invites
        // rows into this same list so a manager sees one unified roster
        // instead of two — isInvite distinguishes "no Cairn account yet,
        // needs Resend/Revoke" from the existing "has an account, hasn't
        // logged in to accept yet" pending-member case above.
        isInvite:  m.is_invite ?? false,
        inviteId:  m.invite_id ?? null,
        dirty:     false,
      })));

      // Seat info — non-fatal if subscription table not yet created
      try {
        const { data: sub } = await supabase
          .rpc('get_clinic_subscription_status', { p_clinic_id: clinic.id })
          .maybeSingle();
        if (sub) setSeatInfo(sub);
      } catch { /* subscription system not yet configured */ }

    } catch (e) {
      console.error('PermissionsView load error:', e);
      setError(t('permissions.loadError'));
    } finally {
      setLoading(false);
    }
  }

  function toggleExisting(profileId, field) {
    if (field === 'isManager') {
      const member = members.find(m => m.profileId === profileId);
      if (member?.isManager && !member.isPending) {
        const managerCount = members.filter(m => m.isManager && !m.isPending).length;
        if (managerCount <= 1) {
          setLastManagerBlocked(profileId);
          return;
        }
      }
    }
    setLastManagerBlocked(null);
    setMembers(prev => prev.map(m =>
      m.profileId === profileId ? { ...m, [field]: !m[field], dirty: true } : m
    ));
    setError(''); setSaved(false); setBillingBlocked(false);
  }

  function addNewRow() {
    setNewRows(prev => [...prev, { tempId: Date.now(), email: '', isDoctor: false, isManager: false }]);
    setSaved(false);
  }

  function updateNewRow(tempId, field, value) {
    setNewRows(prev => prev.map(r => r.tempId === tempId ? { ...r, [field]: value } : r));
    setError(''); setSaved(false); setBillingBlocked(false);
  }

  function removeNewRow(tempId) {
    setNewRows(prev => prev.filter(r => r.tempId !== tempId));
  }

  async function handleApply() {
    setError(''); setSaving(true); setBillingBlocked(false);

    const validNewRows = newRows.filter(r => r.email.trim());

    // Seat check: count all doctors after proposed changes.
    // Independent onboarding/billing audit (2026-09-10), release blocker
    // #3: previously this whole check was skipped when seatInfo was null
    // (no subscription row yet — get_clinic_subscription_status inner-
    // joins clinic_subscriptions and returns nothing) — a non-beta clinic
    // with literally no subscription got unlimited Doctors client-side.
    // Missing seatInfo now means zero seats, matching migration 211's
    // server-side fix (enforce_clinic_seat_limit no longer defaults a
    // missing subscription to one free seat either).
    const existingDoctorCount = members.filter(m => m.isDoctor).length;
    const newDoctorCount      = validNewRows.filter(r => r.isDoctor).length;
    const totalDoctors        = existingDoctorCount + newDoctorCount;
    const seatCap = seatInfo ? seatInfo.seat_count : 0;

    if (clinic.billing_program !== 'beta' && totalDoctors > seatCap) {
      if (!seatInfo) {
        setError(t('permissions.subscriptionRequiredError'));
      } else {
        const over = totalDoctors - seatCap;
        setError(t('permissions.seatLimitError', {
          seatWord: t('permissions.doctorSeats', { count: seatCap }),
          totalDoctors,
          over,
          boxWord: t('permissions.doctorBoxes', { count: over }),
        }));
      }
      setBillingBlocked(true);
      setSaving(false);
      return;
    }

    try {
      // One RPC, one PostgreSQL transaction: either every role/invitation
      // change commits or none does. The function rechecks manager access,
      // locks the clinic, enforces seats, and prevents a zero-manager result.
      const { data: result, error: rosterError } = await supabase.rpc('apply_clinic_roster_changes', {
        p_clinic_id: clinic.id,
        p_existing_changes: members.filter(m => m.dirty).map(m => ({
          member_id: m.memberId,
          is_doctor: m.isDoctor,
          is_manager: m.isManager,
        })),
        p_new_members: validNewRows
          .filter(row => row.isDoctor || row.isManager)
          .map(row => ({
            email: row.email.trim(),
            is_doctor: row.isDoctor,
            is_manager: row.isManager,
          })),
      });
      if (rosterError) throw rosterError;
      if (result?.ok === false && result?.error === 'already_active_member') {
        setError(t('permissions.alreadyActiveMember', { count: result.count || 1 }));
        return;
      }
      if (result?.ok === false && result?.error === 'multi_clinic_not_supported') {
        setError(t('permissions.multiClinicNotSupported', { count: result.count || 1 }));
        return;
      }
      // Server-side backstop (migration 211) for the same seat check
      // above — reachable if seatInfo was stale (e.g. a second manager
      // tab already spent the last seat) rather than the common case.
      if (result?.ok === false && result?.error === 'subscription_required') {
        setError(t('permissions.subscriptionRequiredError'));
        setBillingBlocked(true);
        return;
      }
      if (result?.ok === false && result?.error === 'seat_limit_reached') {
        setError(t('permissions.seatLimitError', {
          seatWord: t('permissions.doctorSeats', { count: result.seat_count || 0 }),
          totalDoctors: result.resulting_doctors || 0,
          over: (result.resulting_doctors || 0) - (result.seat_count || 0),
          boxWord: t('permissions.doctorBoxes', { count: (result.resulting_doctors || 0) - (result.seat_count || 0) }),
        }));
        setBillingBlocked(true);
        return;
      }

      // Brand-new-Doctor invitations (audit finding #2, 2026-09-10):
      // apply_clinic_roster_changes just created a durable clinic_invites
      // row for each of these emails (no existing Cairn account) — the
      // actual invite email is a separate step (Auth Admin API, service-
      // role only) since RPCs can't make outbound HTTP calls themselves.
      // Awaited (not fire-and-forget) so a send failure can be reported —
      // the invite row itself already exists either way, so this never
      // blocks the "saved" state below, only adds a warning alongside it.
      const newInvites = result?.new_invites || [];
      let inviteSendFailures = 0;
      for (const email of newInvites) {
        try {
          const { error: sendErr } = await supabase.functions.invoke('send-clinic-invite-email', {
            body: { clinic_id: clinic.id, email },
          });
          if (sendErr) { inviteSendFailures++; console.warn('Clinic invite email failed:', email, sendErr); }
        } catch (sendErr) {
          inviteSendFailures++;
          console.warn('Clinic invite email failed:', email, sendErr);
        }
      }
      setSaved(true);
      setNewRows([]);
      await load();
      // load() clears `error` at its own start (its normal "clean slate on
      // every load" behavior) — this warning is set after it returns so it
      // isn't immediately wiped out by that.
      if (inviteSendFailures > 0) {
        setError(t('permissions.inviteEmailFailed', { count: inviteSendFailures }));
      }
    } catch (e) {
      console.error('PermissionsView save error:', e);
      setError(t('permissions.saveError'));
    } finally {
      setSaving(false);
    }
  }

  async function handleResendInvite(inviteId) {
    setError(''); setSaved(false);
    try {
      // clinic_invites has no direct client SELECT/UPDATE at all (migration
      // 212 revokes it entirely, same posture as clinic_members) — the
      // RPC's own return value is the only way to get the email back for
      // the follow-up send-clinic-invite-email call below.
      const { data: result, error: err } = await supabase.rpc('resend_clinic_invite', { p_invite_id: inviteId });
      if (err) throw err;
      if (result?.email) {
        const { error: sendErr } = await supabase.functions.invoke('send-clinic-invite-email', {
          body: { clinic_id: result.clinic_id || clinic.id, email: result.email },
        });
        if (sendErr) { setError(t('permissions.inviteEmailFailed', { count: 1 })); return; }
      }
      setSaved(true);
    } catch (e) {
      console.error('Resend invite failed:', e);
      setError(t('permissions.resendInviteFailed'));
    }
  }

  async function handleRevokeInvite(inviteId) {
    setError(''); setSaved(false);
    try {
      const { error: err } = await supabase.rpc('revoke_clinic_invite', { p_invite_id: inviteId });
      if (err) throw err;
      await load();
    } catch (e) {
      console.error('Revoke invite failed:', e);
      setError(t('permissions.revokeInviteFailed'));
    }
  }

  const totalSelectedDoctors =
    members.filter(m => m.isDoctor).length +
    newRows.filter(r => r.isDoctor && r.email.trim()).length;

  const th = {
    fontSize: 11, fontWeight: 600, color: c.textSoft,
    textTransform: 'uppercase', letterSpacing: 0.5,
  };
  const grid = { display: 'grid', gridTemplateColumns: '1fr 1fr 100px 100px 32px', alignItems: 'center' };

  if (loading) {
    return <div style={{ padding: 40, color: c.textSoft }}>{t('permissions.loadingStaff')}</div>;
  }

  return (
    <div style={{ maxWidth: 780 }}>
      <div style={{ marginBottom: 20 }}>
        <h1 style={{ fontSize: 24, fontWeight: 700, color: c.slate, margin: '0 0 12px' }}>{t('permissions.title')}</h1>
        <div style={{ backgroundColor: c.white, borderRadius: 10, border: `1px solid ${c.border}`, padding: '14px 18px', fontSize: 13, color: c.textSoft, lineHeight: 1.7 }}>
          <div><strong style={{ color: c.slate }}>{t('permissions.isManager')}</strong> {t('permissions.managersDescription')}</div>
          <div><strong style={{ color: c.slate }}>{t('permissions.isDoctor')}</strong> {t('permissions.doctorsDescription')}</div>
          <div style={{ marginTop: 4, fontSize: 12 }}>{t('permissions.seatNote')}</div>
        </div>
      </div>

      <div style={{ backgroundColor: c.white, borderRadius: 12, border: `1px solid ${c.border}`, overflow: 'hidden' }}>

        {/* Header row */}
        <div style={{ ...grid, padding: '10px 20px', backgroundColor: c.fog, borderBottom: `1px solid ${c.border}` }}>
          <div style={th}>{t('permissions.colName')}</div>
          <div style={th}>{t('permissions.colEmail')}</div>
          <div style={{ ...th, textAlign: 'center' }}>{t('permissions.isManager')}</div>
          <div style={{ ...th, textAlign: 'center' }}>
            <div>{t('permissions.isDoctor')}</div>
            {seatInfo && (
              <div style={{ fontSize: 10, fontWeight: 400, color: totalSelectedDoctors > seatInfo.seat_count ? c.error : c.textSoft, textTransform: 'none', letterSpacing: 0, marginTop: 2 }}>
                {t('permissions.seats', { used: totalSelectedDoctors, total: seatInfo.seat_count })}
              </div>
            )}
          </div>
          <div />
        </div>

        {/* Existing members */}
        {members.length === 0 && newRows.length === 0 && (
          <div style={{ padding: '32px 20px', textAlign: 'center', color: c.textSoft, fontSize: 13 }}>
            {t('permissions.emptyState')}
          </div>
        )}

        {members.map((m, i) => {
          const willBeRemoved = m.dirty && !m.isDoctor && !m.isManager;
          const isSelf = m.profileId === profile?.id;
          // Brand-new-Doctor invitations (audit finding #2, 2026-09-10):
          // no clinic_members row exists yet to toggle, so this row shows
          // its roles as plain text and Resend/Revoke instead of the
          // usual checkboxes — editing roles means revoking and
          // re-inviting with the roles you want, same as re-submitting
          // the add-staff-member form refreshes an existing pending
          // invite's roles (apply_clinic_roster_changes's ON CONFLICT).
          if (m.isInvite) {
            return (
              <div
                key={`invite-${m.inviteId}`}
                style={{ ...grid, padding: '14px 20px', borderBottom: `1px solid ${c.border}`, backgroundColor: i % 2 === 0 ? c.white : c.fog }}
              >
                <div style={{ fontSize: 14, color: c.textSoft, fontStyle: 'italic' }}>
                  {t('permissions.invitedNoAccount')}
                </div>
                <div style={{ fontSize: 13, color: c.textSoft }}>{m.email}</div>
                <div style={{ textAlign: 'center', fontSize: 13, color: m.isManager ? c.moss : c.border }}>{m.isManager ? '✓' : '—'}</div>
                <div style={{ textAlign: 'center', fontSize: 13, color: m.isDoctor ? c.moss : c.border }}>{m.isDoctor ? '✓' : '—'}</div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 4, alignItems: 'flex-end' }}>
                  <button onClick={() => handleResendInvite(m.inviteId)} title={t('permissions.resend')}
                    style={{ background: 'none', border: 'none', cursor: 'pointer', color: c.moss, fontSize: 11, padding: 0, whiteSpace: 'nowrap' }}>
                    {t('permissions.resend')}
                  </button>
                  <button onClick={() => handleRevokeInvite(m.inviteId)} title={t('permissions.revoke')}
                    style={{ background: 'none', border: 'none', cursor: 'pointer', color: c.error, fontSize: 11, padding: 0, whiteSpace: 'nowrap' }}>
                    {t('permissions.revoke')}
                  </button>
                </div>
              </div>
            );
          }
          return (
            <div
              key={m.memberId}
              style={{
                ...grid,
                padding: '14px 20px',
                borderBottom: `1px solid ${c.border}`,
                backgroundColor: willBeRemoved ? '#fdf0f0' : i % 2 === 0 ? c.white : c.fog,
                opacity: willBeRemoved ? 0.7 : 1,
              }}
            >
              <div style={{ fontSize: 14, color: c.slate, fontWeight: 500 }}>
                {m.name}
                {isSelf && (
                  <span style={{ marginLeft: 6, fontSize: 10, color: c.textSoft, fontWeight: 400 }}>{t('permissions.you')}</span>
                )}
                {willBeRemoved && (
                  <span style={{ marginLeft: 6, fontSize: 10, color: c.error }}>{t('permissions.willBeRemoved')}</span>
                )}
                {m.isPending && !willBeRemoved && (
                  <span style={{ marginLeft: 6, fontSize: 10, color: c.sandstoneText }}>{t('permissions.invitePending')}</span>
                )}
              </div>
              <div style={{ fontSize: 13, color: c.textSoft }}>{m.email}</div>

              {/* Manager checkbox — the last-manager guard (toggleExisting,
                  managerCount <= 1) is the only lock now that is_admin is
                  retired; it already covers this generically (self or not). */}
              <div style={{ textAlign: 'center' }}>
                <input
                  type="checkbox"
                  checked={m.isManager}
                  onChange={() => toggleExisting(m.profileId, 'isManager')}
                  aria-label={t('permissions.managerAccessA11y', { name: m.name })}
                  style={{ width: 16, height: 16, accentColor: c.moss, cursor: 'pointer' }}
                />
                {lastManagerBlocked === m.profileId && (
                  <div style={{ fontSize: 10, color: c.error, marginTop: 3, lineHeight: 1.3 }}>
                    {t('permissions.lastManagerLine1')}<br />{t('permissions.lastManagerLine2')}
                  </div>
                )}
              </div>

              {/* Doctor checkbox */}
              <div style={{ textAlign: 'center' }}>
                <input
                  type="checkbox"
                  checked={m.isDoctor}
                  onChange={() => toggleExisting(m.profileId, 'isDoctor')}
                  aria-label={t('permissions.doctorAccessA11y', { name: m.name })}
                  style={{ width: 16, height: 16, accentColor: c.moss, cursor: 'pointer' }}
                />
              </div>

              <div />
            </div>
          );
        })}

        {/* New staff rows */}
        {newRows.map(row => (
          <div
            key={row.tempId}
            style={{
              ...grid,
              padding: '10px 20px',
              borderBottom: `1px solid ${c.border}`,
              backgroundColor: '#f0f4ee',
            }}
          >
            <div style={{ fontSize: 12, color: c.moss, fontWeight: 600 }}>{t('permissions.newBadge')}</div>
            <input
              type="email"
              placeholder={t('permissions.emailPlaceholder')}
              value={row.email}
              onChange={e => updateNewRow(row.tempId, 'email', e.target.value)}
              aria-label={t('permissions.newEmailA11y')}
              style={{
                fontSize: 13, padding: '6px 10px', borderRadius: 6,
                border: `1px solid ${c.border}`, outline: 'none',
                color: c.slate, width: '88%', backgroundColor: c.white,
              }}
            />
            <div style={{ textAlign: 'center' }}>
              <input
                type="checkbox"
                checked={row.isManager}
                onChange={() => updateNewRow(row.tempId, 'isManager', !row.isManager)}
                aria-label={t('permissions.managerAccessA11y', { name: row.email || t('permissions.newStaffFallback') })}
                style={{ width: 16, height: 16, accentColor: c.moss, cursor: 'pointer' }}
              />
            </div>
            <div style={{ textAlign: 'center' }}>
              <input
                type="checkbox"
                checked={row.isDoctor}
                onChange={() => updateNewRow(row.tempId, 'isDoctor', !row.isDoctor)}
                aria-label={t('permissions.doctorAccessA11y', { name: row.email || t('permissions.newStaffFallback') })}
                style={{ width: 16, height: 16, accentColor: c.moss, cursor: 'pointer' }}
              />
            </div>
            <button
              onClick={() => removeNewRow(row.tempId)}
              title={t('permissions.removeRowTitle')}
              style={{ background: 'none', border: 'none', cursor: 'pointer', color: c.textSoft, fontSize: 18, lineHeight: 1, padding: 0 }}
            >×</button>
          </div>
        ))}

        {/* Add staff member button */}
        <div style={{ padding: '14px 20px', borderTop: `1px solid ${c.border}` }}>
          <button
            onClick={addNewRow}
            style={{
              padding: '8px 18px', border: `1px solid ${c.moss}`, borderRadius: 8,
              background: 'none', color: c.moss, fontSize: 13, fontWeight: 600,
              cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6,
            }}
          >
            <span style={{ fontSize: 18, lineHeight: 1 }}>+</span>
            {t('permissions.addStaffMember')}
          </button>
        </div>
      </div>

      {/* Footer: apply */}
      <div style={{ marginTop: 14, display: 'flex', alignItems: 'center', justifyContent: 'flex-end', flexWrap: 'wrap', gap: 10 }}>
        <div style={{ fontSize: 13, color: c.textSoft }}>
          {clinic.billing_program === 'beta' && (
            <span style={{ color: c.moss }}>{t('permissions.betaSeats')}</span>
          )}
          {clinic.billing_program !== 'beta' && !seatInfo && (
            <span style={{ color: c.sandstoneText }}>
              {t('permissions.noSubscription')}
              {onGoToBilling && (
                <button onClick={onGoToBilling} style={{ marginLeft: 8, background: 'none', border: 'none', padding: 0, color: c.sandstoneText, fontSize: 13, fontWeight: 600, textDecoration: 'underline', cursor: 'pointer' }}>
                  {t('permissions.goToBilling')}
                </button>
              )}
            </span>
          )}
          {clinic.billing_program !== 'beta' && seatInfo && totalSelectedDoctors > seatInfo.seat_count && (
            <span style={{ color: c.error }}>
              {t('permissions.overLimit')}
              {onGoToBilling && (
                <button onClick={onGoToBilling} style={{ marginLeft: 8, background: 'none', border: 'none', padding: 0, color: c.error, fontSize: 13, fontWeight: 600, textDecoration: 'underline', cursor: 'pointer' }}>
                  {t('permissions.goToBilling')}
                </button>
              )}
            </span>
          )}
        </div>
        <button
          onClick={handleApply}
          disabled={saving}
          style={{
            padding: '10px 28px', backgroundColor: c.moss, color: c.white,
            border: 'none', borderRadius: 8, fontSize: 14, fontWeight: 600,
            cursor: saving ? 'default' : 'pointer', opacity: saving ? 0.6 : 1,
          }}
        >
          {saving ? t('permissions.saving') : t('permissions.apply')}
        </button>
      </div>

      {error && (
        <div role="alert" style={{
          marginTop: 10, padding: '10px 14px',
          backgroundColor: c.redLight, border: `1px solid ${c.error}`,
          borderRadius: 8, fontSize: 13, color: c.error,
          display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap',
        }}>
          <span>{error}</span>
          {/* Independent onboarding/billing audit (2026-09-10): seat
              errors previously had no path to Billing at all — a manager
              had to already know where to look. */}
          {billingBlocked && onGoToBilling && (
            <button
              onClick={onGoToBilling}
              style={{ padding: '6px 14px', backgroundColor: c.error, color: c.white, border: 'none', borderRadius: 6, fontSize: 12, fontWeight: 600, cursor: 'pointer', flexShrink: 0 }}
            >
              {t('permissions.goToBilling')}
            </button>
          )}
        </div>
      )}

      {saved && !error && (
        <div style={{
          marginTop: 10, padding: '10px 14px',
          backgroundColor: c.greenLight, border: `1px solid ${c.moss}`,
          borderRadius: 8, fontSize: 13, color: c.moss,
        }}>
          {t('permissions.saved')}
        </div>
      )}
    </div>
  );
}
